import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Presentation,
  LayoutDashboard,
  Wallet,
  TrendingUp,
  Briefcase,
  Users,
  HandCoins,
  PiggyBank,
  RefreshCcw,
  ShieldAlert
} from 'lucide-react'
import { useModuleView } from '../../hooks/useModuleView'
import managementService from '../../services/managementService'
import './Management.css'

export const MANAGEMENT_REPORTS = [
  { key: 'directors-review', title: "Director's Business Review", description: 'Interactive 18-screen directors’ meeting deck with snapshots, comparison and decisions', icon: Presentation, featured: true },
  { key: 'mis-snapshot', title: 'Management MIS Snapshot', description: 'One-page health, signals and headline KPIs', icon: LayoutDashboard },
  { key: 'financial-health', title: 'Financial Health and Runway', description: 'Cash, burn, runway, receivables and budget variance', icon: Wallet },
  { key: 'sales-pipeline', title: 'Sales, Pipeline and Revenue', description: 'Invoicing trend, pipeline stages and customer concentration', icon: TrendingUp },
  { key: 'project-portfolio', title: 'Project Portfolio Health', description: 'Delivery health, milestones, billing and margin', icon: Briefcase },
  { key: 'resource-capacity', title: 'Resource Capacity and Utilisation', description: 'Allocation, bench, utilisation and timesheet compliance', icon: Users },
  { key: 'customer-collection', title: 'Customer and Collection Health', description: 'Receivables ageing, DSO and overdue customers', icon: HandCoins },
  { key: 'expense-budget', title: 'Expense and Budget Performance', description: 'Burn vs approved budget by category and month', icon: PiggyBank },
  { key: 'renewals', title: 'Renewals and Recurring Commitments', description: 'Subscription renewals, notice deadlines and contract renewals', icon: RefreshCcw },
  { key: 'risk-actions', title: 'Risk, Compliance and Action Tracker', description: 'Risk register, action tracker and compliance gaps', icon: ShieldAlert }
]

const Management = () => {
  const navigate = useNavigate()
  const moduleView = useModuleView()
  const [access, setAccess] = useState(null)
  const [accessError, setAccessError] = useState(false)

  useEffect(() => {
    if (moduleView !== 'reports') navigate('/management?tab=reports', { replace: true })
  }, [moduleView, navigate])

  useEffect(() => {
    let cancelled = false
    managementService.getAccessCached()
      .then((res) => { if (!cancelled && res?.success) setAccess(res.data) })
      .catch(() => { if (!cancelled) setAccessError(true) })
    return () => { cancelled = true }
  }, [])

  const noView = access && !access.isAdmin && !(access.permissions || []).includes('VIEW')

  return (
    <div className="sales-dashboard">
      <div className="page-header" style={{ textAlign: 'left' }}>
        <div className="page-title" style={{ textAlign: 'left', alignItems: 'flex-start' }}>
          <h1>Management</h1>
          <p style={{ textAlign: 'left', margin: 0 }}>Management MIS and director review built from live ERP data</p>
        </div>
      </div>

      {noView && (
        <div className="alert alert-warning management-access-banner">
          You do not have Management MIS view permission. Ask an administrator to grant <strong>MANAGEMENT_MIS · VIEW</strong>.
        </div>
      )}
      {accessError && (
        <div className="alert alert-info management-access-banner">
          Could not verify Management MIS access. Reports will show an error if access is denied.
        </div>
      )}

      {!noView && (
        <div className="tab-content">
          <div className="management-reports-grid">
            {MANAGEMENT_REPORTS.map((r) => {
              const Icon = r.icon
              const go = () => navigate(`/management/reports/${r.key}`)
              return (
                <div
                  key={r.key}
                  className={`management-report-card ${r.featured ? 'featured' : ''}`}
                  role="button"
                  tabIndex={0}
                  onClick={go}
                  onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), go())}
                >
                  <div className="management-report-icon"><Icon size={24} /></div>
                  <div className="management-report-content">
                    <h3>{r.title}</h3>
                    <p>{r.description}</p>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

export default Management
